import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Autocomplete,
  Fab,
  Chip,
  Tooltip,
  Snackbar,
  Alert,
  Dialog as ConfirmDialog,
  DialogTitle as ConfirmDialogTitle,
  DialogActions as ConfirmDialogActions,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { API_BASE_URL } from '../../../constants/constants';
import { usePermissions } from '../../../context/PermissionContext';
import BackButton from '../../../components/BackButton';

const PINCODE_SAMPLE_CSV = 'pincode\n600001\n600002\n600003\n';
const BULK_CHUNK_SIZE = 200;

// Parse uploaded CSV/TXT text into valid pincodes and invalid lines (with reason).
// Each cell (comma / semicolon / tab / whitespace separated) is treated as one pincode.
const parsePincodeFile = (text) => {
  const valid = [];
  const invalid = [];
  const firstSeen = new Map();
  const lines = String(text || '').replace(/^\uFEFF/, '').split(/\r\n|\n|\r/);
  lines.forEach((rawLine, i) => {
    const lineNo = i + 1;
    const cells = rawLine.split(/[,;\t\s]+/).map((c) => c.replace(/^["']+|["']+$/g, '').trim()).filter(Boolean);
    cells.forEach((cell) => {
      if (/^pin\s*code$|^pincodes?$/i.test(cell)) return; // header
      if (!/^\d{6}$/.test(cell)) {
        invalid.push({ line: lineNo, value: cell, reason: 'Not a 6-digit number' });
        return;
      }
      if (cell[0] === '0') {
        invalid.push({ line: lineNo, value: cell, reason: 'Pincode cannot start with 0' });
        return;
      }
      if (firstSeen.has(cell)) {
        invalid.push({ line: lineNo, value: cell, reason: `Duplicate in file (first on line ${firstSeen.get(cell)})` });
        return;
      }
      firstSeen.set(cell, lineNo);
      valid.push(cell);
    });
  });
  return { valid, invalid };
};

// Group backend response messages into added / already here / in another area / other.
const groupPincodeMessages = (messages) => {
  const groups = { added: [], existsHere: [], existsElsewhere: [], other: [] };
  (messages || []).forEach((m) => {
    const text = String(m);
    const pin = (text.match(/Pincode\s+(\d+)/i) || [])[1] || '';
    if (/added successfully/i.test(text)) groups.added.push(pin || text);
    else if (/already exists in the current area/i.test(text)) groups.existsHere.push(pin || text);
    else if (/exists in another area/i.test(text)) {
      const area = (text.split(/exists in another area:\s*/i)[1] || '').replace(/\.$/, '');
      groups.existsElsewhere.push({ pin, area });
    } else groups.other.push(text);
  });
  return groups;
};

const CreatePincode = () => {
  const [pincodes, setPincodes] = useState([]);
  const [open, setOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [taluks, setTaluks] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedTaluk, setSelectedTaluk] = useState(null);
  const [inputPincodes, setInputPincodes] = useState([]);
  const [newPincode, setNewPincode] = useState('');
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');
  const [message, setMessage] = useState('');
  const { can } = usePermissions();

  // Bulk upload state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDistrict, setBulkDistrict] = useState(null);
  const [bulkTaluk, setBulkTaluk] = useState(null);
  const [bulkFileName, setBulkFileName] = useState('');
  const [bulkParsed, setBulkParsed] = useState(null); // { valid, invalid }
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [bulkResult, setBulkResult] = useState(null); // grouped messages
  const [bulkError, setBulkError] = useState('');

  useEffect(() => {
    fetchPincodes();
    fetchDistricts();
  }, []);

  const fetchPincodes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/pincodes/details`);
      if (Array.isArray(response.data)) {
        setPincodes(response.data);
      } else {
        setPincodes([]);
      }
    } catch (error) {
      console.error('Error fetching pincodes:', error);
      setPincodes([]);
    }
  };

  const fetchDistricts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/districts`);
      setDistricts(response.data);
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  };

  const fetchTaluks = async (districtId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/taluks/district/${districtId}`);
      setTaluks(response.data);
    } catch (error) {
      console.error('Error fetching taluks:', error);
    }
  };

  const handleDistrictChange = (event, value) => {
    setSelectedDistrict(value);
    setSelectedTaluk(null);
    setInputPincodes([]);
    setNewPincode('');
    if (value) {
      fetchTaluks(value._id);
    }
  };

  const handleTalukChange = (event, value) => {
    setSelectedTaluk(value);
    setInputPincodes([]);
    setNewPincode('');
  };

  const handleNewPincodeChange = (event) => {
    const value = event.target.value.replace(/\D/g, '').substring(0, 6);
    setNewPincode(value);
    setError('');
  };

  const handleAddNewPincode = (event) => {
    if ((event.key === 'Enter' || event.key === ',' || event.type === 'click') && newPincode.length === 6) {
      if (!inputPincodes.includes(newPincode)) {
        setInputPincodes([...inputPincodes, newPincode]);
        setNewPincode('');
      } else {
        setSnackbarSeverity('error');
        setMessage('Pincode already exists.');
        setSnackbarOpen(true);
      }
    } else if (newPincode.length !== 6 && (event.key === 'Enter' || event.key === ',' || event.type === 'click')) {
      setSnackbarSeverity('error');
      setMessage('Invalid pincode. Must be 6 digits.');
      setSnackbarOpen(true);
    }
  };

  const handleOpenDialog = () => {
    if (!can('master.pincodeCreate.create')) return;
    setOpen(true);
  };

  const handleCloseDialog = () => {
    if (inputPincodes.length > 0) {
      setConfirmDialogOpen(true);
    } else {
      clearDialogData();
    }
  };

  const clearDialogData = () => {
    setOpen(false);
    setSelectedDistrict(null);
    setSelectedTaluk(null);
    setInputPincodes([]);
    setNewPincode('');
    setError('');
  };

  const handleSubmit = async () => {
    if (!can('master.pincodeCreate.create')) return;
    if (selectedDistrict && selectedTaluk && inputPincodes.length > 0) {
      try {
        const numericPincodes = inputPincodes.map(pincode => Number(pincode));
  
        const response = await axios.post(`${API_BASE_URL}/pincodes`, {
          districtId: selectedDistrict._id,
          talukId: selectedTaluk._id,
          pincodes: numericPincodes,
        });
  
        setSnackbarSeverity('success');
        setMessage(response.data.messages.join('\n'));
        setSnackbarOpen(true);
        clearDialogData();
        fetchPincodes();
      } catch (error) {
        console.error('Error submitting pincodes:', error);
        setSnackbarSeverity('error');
        setMessage((error.response && error.response.data && error.response.data.message) || 'Error submitting pincodes');
        setSnackbarOpen(true);
      }
    } else {
      setSnackbarSeverity('error');
      setMessage('Please fill all fields before submitting.');
      setSnackbarOpen(true);
    }
  };

  const handleExportCSV = () => {
    if (!can('master.pincodeCreate.export')) return;
    if (!pincodes || pincodes.length === 0) return;
    const headers = ['SL', 'District', 'Constituency', 'Pincodes'];
    const csvRows = [headers.join(',')];

    pincodes.forEach((p, idx) => {
      const sl = idx + 1;
      const dName = p.districtName || 'N/A';
      const cName = p.talukName || 'N/A';
      const pins = Array.isArray(p.pincodes) ? p.pincodes.join(';') : '';

      csvRows.push([sl, `"${dName}"`, `"${cName}"`, `"${pins}"`].join(','));
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `pincodes_report_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // ---------- Bulk upload ----------
  const resetBulk = () => {
    setBulkDistrict(null);
    setBulkTaluk(null);
    setBulkFileName('');
    setBulkParsed(null);
    setBulkSubmitting(false);
    setBulkProgress(0);
    setBulkResult(null);
    setBulkError('');
  };

  const handleOpenBulk = () => {
    if (!can('master.pincodeCreate.create')) return;
    resetBulk();
    setTaluks([]);
    setBulkOpen(true);
  };

  const handleCloseBulk = () => {
    if (bulkSubmitting) return;
    setBulkOpen(false);
    resetBulk();
  };

  const handleBulkDistrictChange = (event, value) => {
    setBulkDistrict(value);
    setBulkTaluk(null);
    setBulkResult(null);
    setTaluks([]);
    if (value) fetchTaluks(value._id);
  };

  const handleBulkTalukChange = (event, value) => {
    setBulkTaluk(value);
    setBulkResult(null);
  };

  const handleBulkFile = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = ''; // allow re-selecting the same file
    setBulkResult(null);
    setBulkError('');
    if (!file) return;
    if (!/\.(csv|txt)$/i.test(file.name)) {
      setBulkParsed(null);
      setBulkFileName('');
      setBulkError('Please upload a .csv or .txt file. Excel sheets can be saved as CSV (File > Save As > CSV).');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parsePincodeFile(reader.result);
      setBulkFileName(file.name);
      setBulkParsed(parsed);
      if (parsed.valid.length === 0) setBulkError('No valid pincodes found in the file.');
    };
    reader.onerror = () => setBulkError('Could not read the file.');
    reader.readAsText(file);
  };

  const handleDownloadSample = () => {
    const blob = new Blob([PINCODE_SAMPLE_CSV], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'pincode_bulk_upload_sample.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleBulkSubmit = async () => {
    if (!can('master.pincodeCreate.create')) return;
    if (!bulkDistrict || !bulkTaluk || !bulkParsed || bulkParsed.valid.length === 0) {
      setBulkError('Select District, Constituency and a file with at least one valid pincode.');
      return;
    }
    setBulkSubmitting(true);
    setBulkError('');
    setBulkProgress(0);
    const all = bulkParsed.valid.map((p) => Number(p));
    const collected = [];
    try {
      for (let i = 0; i < all.length; i += BULK_CHUNK_SIZE) {
        const chunk = all.slice(i, i + BULK_CHUNK_SIZE);
        const response = await axios.post(`${API_BASE_URL}/pincodes`, {
          districtId: bulkDistrict._id,
          talukId: bulkTaluk._id,
          pincodes: chunk,
        });
        const msgs = (response.data && Array.isArray(response.data.messages)) ? response.data.messages : [];
        collected.push(...msgs);
        setBulkProgress(Math.round(((i + chunk.length) / all.length) * 100));
      }
      setBulkResult(groupPincodeMessages(collected));
      fetchPincodes();
    } catch (err) {
      console.error('Error bulk uploading pincodes:', err);
      const msg = (err.response && err.response.data && err.response.data.message) || 'Error uploading pincodes';
      setBulkError(collected.length ? `${msg} (partially processed: ${collected.length} of ${all.length})` : msg);
      if (collected.length) {
        setBulkResult(groupPincodeMessages(collected));
        fetchPincodes();
      }
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false);
  };

  const handleConfirmDialogProceed = () => {
    setConfirmDialogOpen(false);
    clearDialogData();
  };

  const columns = [
    {
      field: 'sl',
      headerName: 'SL',
      width: 70,
      sortable: false,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748b' }}>
          {params.row.sl}
        </Typography>
      ),
    },
    { field: 'districtName', headerName: 'District', width: 190 },
    { field: 'talukName', headerName: 'Constituency', width: 180 },
    {
      field: 'pincodes',
      headerName: 'Pincodes',
      width: 350,
      renderCell: (params) => {
        const pincodes = params.value || [];
        return (
          <div style={{ 
            whiteSpace: 'normal', 
            wordBreak: 'break-word', 
            padding: '8px 0',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Typography variant="body2" style={{ lineHeight: 1.5 }}>
              {pincodes.join(', ')}
            </Typography>
          </div>
        );
      },
    },
  ];

  // Backend returns one merged row per district+constituency
  const rows = pincodes.map((pincode, index) => ({
    id: pincode.districtId && pincode.talukId ? `${pincode.districtId}-${pincode.talukId}` : `row-${index}`,
    sl: index + 1,
    districtName: pincode.districtName,
    talukName: pincode.talukName,
    pincodes: pincode.pincodes,
  }));

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />
      <br /><br />
      <Box sx={{ height: 400, width: '100%', position: 'relative' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Pincode Management
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, mr: 7, flexWrap: 'wrap' }}>
            {can('master.pincodeCreate.create') && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<UploadFileIcon />}
                onClick={handleOpenBulk}
                sx={{ fontWeight: 700 }}
              >
                Bulk Upload
              </Button>
            )}
            {can('master.pincodeCreate.export') && (
              <Button
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                sx={{ fontWeight: 700 }}
              >
                Export CSV
              </Button>
            )}
          </Box>
        </Box>
        <DataGrid rows={rows} columns={columns} autoHeight getRowHeight={() => 'auto'} />
        {can('master.pincodeCreate.create') && (
          <Tooltip title="Create Pincodes">
            <Fab
              color="primary"
              aria-label="add"
              sx={{ position: 'absolute', top: -7, right: 16 }}
              onClick={handleOpenDialog}
            >
              <AddIcon />
            </Fab>
          </Tooltip>
        )}
        <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="sm">
          <DialogTitle>Create New Pincode</DialogTitle>
          <DialogContent>
            <Autocomplete
              options={districts}
              getOptionLabel={(option) => (option && option.name) ? option.name : ''}
              isOptionEqualToValue={(option, value) => (option && value ? option._id === value._id : false)}
              filterOptions={(options, state) =>
                options.filter(option => (option && option.name) ? option.name.toLowerCase().includes((state.inputValue || '').toLowerCase()) : false)
              }
              value={selectedDistrict}
              onChange={handleDistrictChange}
              renderInput={(params) => <TextField {...params} label="Select District" placeholder="Search or select district..." variant="outlined" />}
              sx={{ mb: 2, mt: 1 }}
            />
            <Autocomplete
              options={taluks}
              getOptionLabel={(option) => (option && option.name) ? option.name : ''}
              isOptionEqualToValue={(option, value) => (option && value ? option._id === value._id : false)}
              filterOptions={(options, state) =>
                options.filter(option => (option && option.name) ? option.name.toLowerCase().includes((state.inputValue || '').toLowerCase()) : false)
              }
              value={selectedTaluk}
              onChange={handleTalukChange}
              renderInput={(params) => <TextField {...params} label="Select Constituency" placeholder="Search or select constituency..." variant="outlined" />}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TextField
                label="Pincode"
                variant="outlined"
                value={newPincode}
                onChange={handleNewPincodeChange}
                onKeyDown={handleAddNewPincode}
                placeholder="Enter 6-digit pincode"
                sx={{ flexGrow: 1, mr: 2 }}
                inputProps={{ maxLength: 6 }}
              />
              <Button onClick={handleAddNewPincode} variant="contained">
                Add Pincode
              </Button>
            </Box>
            <Box sx={{ mb: 2 }}>
              {inputPincodes.map((pincode, index) => (
                <Chip
                  key={index}
                  label={pincode}
                  onDelete={() => setInputPincodes(inputPincodes.filter(pc => pc !== pincode))}
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleSubmit} color="primary" variant="contained">
              Submit
            </Button>
          </DialogActions>
        </Dialog>
        {/* Bulk Upload Dialog */}
        <Dialog open={bulkOpen} onClose={handleCloseBulk} fullWidth maxWidth="sm">
          <DialogTitle>Bulk Upload Pincodes</DialogTitle>
          <DialogContent>
            <Autocomplete
              options={districts}
              getOptionLabel={(option) => (option && option.name) ? option.name : ''}
              isOptionEqualToValue={(option, value) => (option && value ? option._id === value._id : false)}
              value={bulkDistrict}
              onChange={handleBulkDistrictChange}
              disabled={bulkSubmitting}
              renderInput={(params) => <TextField {...params} label="Select District" placeholder="Search or select district..." variant="outlined" />}
              sx={{ mb: 2, mt: 1 }}
            />
            <Autocomplete
              options={taluks}
              getOptionLabel={(option) => (option && option.name) ? option.name : ''}
              isOptionEqualToValue={(option, value) => (option && value ? option._id === value._id : false)}
              value={bulkTaluk}
              onChange={handleBulkTalukChange}
              disabled={bulkSubmitting || !bulkDistrict}
              renderInput={(params) => <TextField {...params} label="Select Constituency" placeholder="Search or select constituency..." variant="outlined" />}
              sx={{ mb: 2 }}
            />

            <Alert severity="info" sx={{ mb: 2 }}>
              Upload a <strong>.csv</strong> or <strong>.txt</strong> file with one 6-digit pincode per line
              (header <code>pincode</code> is optional). Have an Excel sheet? Save it as CSV
              (File &gt; Save As &gt; CSV) and upload that file.
            </Alert>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
              <Button variant="contained" component="label" startIcon={<UploadFileIcon />} disabled={bulkSubmitting}>
                Choose File
                <input hidden type="file" accept=".csv,.txt,text/csv,text/plain" onChange={handleBulkFile} />
              </Button>
              <Button variant="text" startIcon={<DownloadIcon />} onClick={handleDownloadSample}>
                Download sample format
              </Button>
              {bulkFileName && (
                <Typography variant="body2" color="text.secondary">{bulkFileName}</Typography>
              )}
            </Box>

            {bulkError && <Alert severity="error" sx={{ mb: 2 }}>{bulkError}</Alert>}

            {bulkParsed && !bulkResult && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Preview</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Chip color="success" label={`Valid: ${bulkParsed.valid.length}`} />
                  <Chip color={bulkParsed.invalid.length ? 'error' : 'default'} label={`Invalid: ${bulkParsed.invalid.length}`} />
                </Box>
                {bulkParsed.valid.length > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, wordBreak: 'break-word' }}>
                    {bulkParsed.valid.slice(0, 30).join(', ')}{bulkParsed.valid.length > 30 ? ` ... (+${bulkParsed.valid.length - 30} more)` : ''}
                  </Typography>
                )}
                {bulkParsed.invalid.length > 0 && (
                  <Box sx={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #fecaca', borderRadius: 1, bgcolor: '#fef2f2' }}>
                    <List dense disablePadding>
                      {bulkParsed.invalid.map((row, idx) => (
                        <ListItem key={`${row.line}-${idx}`} divider>
                          <ListItemText
                            primary={`Line ${row.line}: "${row.value}"`}
                            secondary={row.reason}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
                {bulkParsed.invalid.length > 0 && bulkParsed.valid.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Invalid lines will be skipped; only the {bulkParsed.valid.length} valid pincode(s) will be submitted.
                  </Typography>
                )}
              </Box>
            )}

            {bulkSubmitting && (
              <Box sx={{ my: 2 }}>
                <LinearProgress variant="determinate" value={bulkProgress} />
                <Typography variant="caption" color="text.secondary">Uploading... {bulkProgress}%</Typography>
              </Box>
            )}

            {bulkResult && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Result</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                  <Chip color="success" label={`Added: ${bulkResult.added.length}`} />
                  <Chip color="info" label={`Already in this constituency: ${bulkResult.existsHere.length}`} />
                  <Chip color="warning" label={`In another area: ${bulkResult.existsElsewhere.length}`} />
                  {bulkResult.other.length > 0 && <Chip label={`Other: ${bulkResult.other.length}`} />}
                </Box>
                {bulkResult.added.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>Added</Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{bulkResult.added.join(', ')}</Typography>
                  </Box>
                )}
                {bulkResult.existsHere.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'info.main' }}>Already in this constituency</Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{bulkResult.existsHere.join(', ')}</Typography>
                  </Box>
                )}
                {bulkResult.existsElsewhere.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'warning.dark' }}>Exists in another area (not added)</Typography>
                    <Box sx={{ maxHeight: 160, overflowY: 'auto' }}>
                      <List dense disablePadding>
                        {bulkResult.existsElsewhere.map((row, idx) => (
                          <ListItem key={`${row.pin}-${idx}`} disableGutters>
                            <ListItemText primary={row.pin} secondary={row.area} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Box>
                )}
                {bulkResult.other.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Divider sx={{ mb: 1 }} />
                    {bulkResult.other.map((m, idx) => (
                      <Typography key={idx} variant="body2" color="text.secondary">{m}</Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseBulk} color="secondary" disabled={bulkSubmitting}>
              {bulkResult ? 'Close' : 'Cancel'}
            </Button>
            {!bulkResult && (
              <Button
                onClick={handleBulkSubmit}
                color="primary"
                variant="contained"
                disabled={bulkSubmitting || !bulkDistrict || !bulkTaluk || !bulkParsed || bulkParsed.valid.length === 0}
              >
                {bulkSubmitting ? 'Uploading...' : `Upload ${bulkParsed ? bulkParsed.valid.length : 0} Pincode(s)`}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={5000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ whiteSpace: 'pre-line' }}>
            {message}
          </Alert>
        </Snackbar>

        <ConfirmDialog
          open={confirmDialogOpen}
          onClose={handleConfirmDialogClose}
        >
          <ConfirmDialogTitle>Unsaved Changes</ConfirmDialogTitle>
          <DialogContent>
            <Typography>You have unsaved changes. Do you really want to close?</Typography>
          </DialogContent>
          <ConfirmDialogActions>
            <Button onClick={handleConfirmDialogClose} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleConfirmDialogProceed} color="primary" variant="contained">
              Proceed
            </Button>
          </ConfirmDialogActions>
        </ConfirmDialog>
      </Box>
    </Box>
  );
};

export default CreatePincode;